import { useEffect, useRef, useState } from "react";

import { MarkdownContent } from "~/components/rich-content/MarkdownContent";
import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { ProjectContentReadabilityControls } from "~/components/rich-content/ProjectContentReadabilityControls";
import { Button } from "~/components/ui/button";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";

export interface ProjectTaskDescriptionProps {
  readonly projectId: number | null;
  readonly description: string;
  readonly draft: string;
  readonly pending: boolean;
  readonly onDraftChange: (description: string) => void;
  readonly onSave: (description: string) => Promise<boolean>;
  /** Host cannot write; hides Edit rather than opening an editor that cannot save. */
  readonly readOnly?: boolean;
}

export function ProjectTaskDescription(props: ProjectTaskDescriptionProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  useEffect(() => {
    if (!editing && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      editButtonRef.current?.focus();
    }
  }, [editing]);
  const finishEditing = () => {
    restoreFocusRef.current = true;
    setEditing(false);
  };
  const cancel = () => {
    props.onDraftChange(props.description);
    setError(null);
    finishEditing();
  };
  const save = async () => {
    if (props.draft.length > 5_000) return;
    setError(null);
    setSaving(true);
    try {
      const saved = await props.onSave(props.draft);
      if (!saved) {
        setError("Couldn’t save the description. Your draft is still here—try again.");
        return;
      }
      finishEditing();
    } catch {
      setError("Couldn’t save the description. Your draft is still here—try again.");
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <div className="group relative -mx-1 rounded-md px-1 py-0.5 transition-colors hover:bg-muted">
        {props.description ? (
          <MarkdownContent variant="compact" className="pr-12">
            {props.description}
          </MarkdownContent>
        ) : (
          <p className="text-sm text-muted-foreground">Add a description…</p>
        )}
        {props.readOnly ? null : (
          <button
            ref={editButtonRef}
            type="button"
            onClick={() => {
              setError(null);
              props.onDraftChange(props.description);
              setEditing(true);
            }}
            disabled={props.pending}
            className={`${RESPONSIVE_TEXT_BUTTON_CLASS} absolute right-0 top-0 flex min-w-touch items-center justify-center rounded-lg px-2 text-sm font-medium text-muted-foreground opacity-100 transition-opacity hover:bg-background hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 md:right-1 md:top-0.5 md:min-w-0 md:rounded md:px-1.5 md:text-xs md:opacity-0 md:focus-visible:opacity-100 md:group-hover:opacity-100`}
          >
            Edit
          </button>
        )}
      </div>
    );
  }
  return (
    <div
      onKeyDownCapture={(event) => {
        if (event.key === "Escape" && !saving) {
          event.preventDefault();
          event.stopPropagation();
          cancel();
        }
      }}
    >
      <MarkdownEditor
        value={props.draft}
        onChange={props.onDraftChange}
        ariaLabel="Task description"
        placeholder="Add a description…"
        autoFocus
        maxLength={5_000}
      />
      {props.projectId === null ? null : (
        <ProjectContentReadabilityControls
          projectId={props.projectId}
          surface="task_description"
          value={props.draft}
          disabled={saving || props.pending}
          onChange={props.onDraftChange}
        />
      )}
      <div className="mt-2 flex items-center gap-[8px] md:gap-2">
        <Button
          type="button"
          size="sm"
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          disabled={saving || props.draft.length > 5_000}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save description"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          disabled={saving}
          onClick={cancel}
        >
          Cancel
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
